/**
 * Message input component for sending messages in a session.
 * Modern glass input bar with gradient send button.
 */

import { useMemo, useRef, useState, type ClipboardEvent, type KeyboardEvent } from 'react';
import { Chip, Button, Textarea, Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@coordio/ui';
import { Paperclip as AttachFileIcon, Send as SendIcon } from 'lucide-react';
import { HarnessMode } from '../../types/session';

interface MessageInputProps {
  onSend: (payload: { content: string; file?: File; mediaType?: string }) => void | Promise<void>;
  disabled?: boolean;
  placeholder?: string;
  /**
   * When set, shows the harness mode picker (Execute | Plan | Auto) for agents running on the
   * Microsoft Agent Framework harness. Hidden for classic agents.
   */
  modePicker?: {
    mode: HarnessMode;
    onChange: (mode: HarnessMode) => void;
  };
}

const MODE_PICKER_OPTIONS: { value: HarnessMode; label: string; hint: string }[] = [
  { value: HarnessMode.Execute, label: 'Execute', hint: 'Do not wait: the agent works its todos autonomously.' },
  { value: HarnessMode.Plan, label: 'Plan', hint: 'The agent outlines todos and waits for your approval.' },
  { value: HarnessMode.Auto, label: 'Auto', hint: 'The agent decides whether to answer, plan or execute.' },
];

type DetectedMediaType = 'image' | 'audio' | 'video' | 'document' | 'unknown';

const MAX_BYTES: Record<Exclude<DetectedMediaType, 'unknown'>, number> = {
  image: 20 * 1024 * 1024,
  audio: 100 * 1024 * 1024,
  video: 500 * 1024 * 1024,
  document: 50 * 1024 * 1024,
};

const detectMediaType = (file: File): DetectedMediaType => {
  const mime = (file.type || '').toLowerCase();
  if (mime.startsWith('image/')) return 'image';
  if (mime.startsWith('audio/')) return 'audio';
  if (mime.startsWith('video/')) return 'video';
  const ext = file.name.split('.').pop()?.toLowerCase() ?? '';
  if (['pdf', 'docx', 'txt'].includes(ext)) return 'document';
  if (['jpg', 'jpeg', 'png', 'gif', 'webp'].includes(ext)) return 'image';
  if (['mp3', 'wav', 'ogg'].includes(ext)) return 'audio';
  if (['mp4', 'avi', 'mov'].includes(ext)) return 'video';
  return 'unknown';
};

const validateFile = (file: File): { ok: boolean; error?: string; mediaType: DetectedMediaType } => {
  const mediaType = detectMediaType(file);
  if (mediaType === 'unknown') {
    return { ok: false, mediaType, error: 'Unsupported file type. Please attach an image, audio, video, or document.' };
  }
  const limit = MAX_BYTES[mediaType];
  if (file.size > limit) {
    return { ok: false, mediaType, error: `File too large for ${mediaType}. Max ${Math.round(limit / (1024 * 1024))} MB.` };
  }
  return { ok: true, mediaType };
};

/**
 * Modern message input with glassmorphism styling and gradient send button.
 */
export const MessageInput = ({ onSend, disabled = false, placeholder = 'Type your message...', modePicker }: MessageInputProps) => {
  const [message, setMessage] = useState('');
  const [attachedFile, setAttachedFile] = useState<File | undefined>(undefined);
  const [attachError, setAttachError] = useState<string | undefined>(undefined);
  const [sending, setSending] = useState(false);
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const attachment = useMemo(() => {
    if (!attachedFile) return undefined;
    const v = validateFile(attachedFile);
    return { ...v, file: attachedFile };
  }, [attachedFile]);

  const handleSend = async () => {
    if (disabled || sending) return;
    const content = message.trim();
    if (!content && !attachedFile) return;

    setSending(true);
    try {
      if (attachedFile) {
        const v = validateFile(attachedFile);
        if (!v.ok) { setAttachError(v.error); setSending(false); return; }
        await onSend({ content, file: attachedFile, mediaType: v.mediaType === 'unknown' ? undefined : v.mediaType });
      } else {
        await onSend({ content });
      }
      setMessage('');
      setAttachedFile(undefined);
      setAttachError(undefined);
    } finally {
      setSending(false);
    }
  };

  const handleKeyPress = (e: KeyboardEvent<HTMLDivElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSend(); }
  };

  const handleFiles = (files: FileList | File[]) => {
    const arr = Array.from(files);
    if (arr.length === 0) return;
    const file = arr[0];
    const v = validateFile(file);
    if (!v.ok) { setAttachError(v.error); setAttachedFile(undefined); return; }
    setAttachError(undefined);
    setAttachedFile(file);
  };

  const handlePaste = (e: ClipboardEvent<HTMLDivElement>) => {
    const items = e.clipboardData?.items;
    if (!items) return;
    for (const item of Array.from(items)) {
      if (item.kind === 'file') {
        const file = item.getAsFile();
        if (file) handleFiles([file]);
      }
    }
  };

  const canSend = !disabled && !sending && (message.trim().length > 0 || !!attachedFile);

  return (
    <div
      className="flex flex-col gap-3 p-6 flex-shrink-0"
      style={{
        borderTop: '1px solid var(--glass-border)',
        background: 'var(--glass-bg)',
        backdropFilter: 'blur(16px)',
        WebkitBackdropFilter: 'blur(16px)',
      }}
      onDragOver={(e) => { e.preventDefault(); e.stopPropagation(); }}
      onDrop={(e) => {
        e.preventDefault(); e.stopPropagation();
        if (disabled) return;
        handleFiles(e.dataTransfer.files);
      }}
    >
      {/* Harness Mode Picker (harness-enabled agents only) */}
      {modePicker && (
        <div className="flex items-center gap-2">
          <span className="text-xs text-muted-foreground font-semibold">
            Mode
          </span>
          <div className="inline-flex rounded-md border border-[var(--color-border-default)]" role="group" aria-label="Harness mode">
            {MODE_PICKER_OPTIONS.map((option) => (
              <TooltipProvider key={option.value}>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <button
                      type="button"
                      disabled={disabled}
                      onClick={() => modePicker.onChange(option.value)}
                      className={`px-2.5 py-0.5 text-xs font-medium transition-colors first:rounded-l-md last:rounded-r-md ${
                        modePicker.mode === option.value
                          ? 'bg-[var(--color-accent)] text-white'
                          : 'bg-[var(--color-bg-surface)] text-[var(--color-text-secondary)] hover:bg-[var(--color-bg-surface-hover)]'
                      } disabled:opacity-50 disabled:cursor-not-allowed`}
                    >
                      {option.label}
                    </button>
                  </TooltipTrigger>
                  <TooltipContent>{option.hint}</TooltipContent>
                </Tooltip>
              </TooltipProvider>
            ))}
          </div>
        </div>
      )}

      {/* Attachment Preview */}
      {attachedFile && (
        <div className="flex items-center gap-2 flex-wrap">
          <Chip
            label={`${attachedFile.name} (${Math.round(attachedFile.size / 1024)} KB)`}
            variant="outlined"
            onRemove={() => setAttachedFile(undefined)}
          />
          {attachment?.mediaType && attachment.mediaType !== 'unknown' && (
            <Chip label={attachment.mediaType} color="accent" variant="outlined" />
          )}
        </div>
      )}

      {attachError && (
        <span className="text-xs text-red-500">
          {attachError}
        </span>
      )}

      {/* Input Row */}
      <div className="flex gap-3 items-end">
        <Textarea
          className="flex-1 resize-none rounded-lg bg-[rgba(255,255,255,0.02)]"
          rows={1}
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          onKeyDown={(e) => handleKeyPress(e as unknown as KeyboardEvent<HTMLDivElement>)}
          onPaste={(e) => handlePaste(e as unknown as ClipboardEvent<HTMLDivElement>)}
          placeholder={placeholder}
          disabled={disabled}
        />

        <input
          ref={fileInputRef}
          type="file"
          hidden
          onChange={(e) => {
            if (!e.target.files) return;
            handleFiles(e.target.files);
            e.target.value = '';
          }}
        />

        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <span>
                <Button
                  variant="ghost"
                  size="icon"
                  disabled={disabled}
                  onClick={() => fileInputRef.current?.click()}
                  className="border border-[var(--glass-border)]"
                >
                  <AttachFileIcon size={16} />
                </Button>
              </span>
            </TooltipTrigger>
            <TooltipContent>Attach file</TooltipContent>
          </Tooltip>
        </TooltipProvider>

        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <span>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={handleSend}
                  disabled={!canSend}
                  style={{
                    background: canSend ? 'var(--gradient-primary)' : 'rgba(148, 163, 184, 0.1)',
                    color: canSend ? '#fff' : undefined,
                    borderRadius: 'var(--mui-shape-customBorderRadius-sm)',
                    transition: 'all 200ms ease',
                  }}
                >
                  <SendIcon size={16} />
                </Button>
              </span>
            </TooltipTrigger>
            <TooltipContent>Send message (Enter)</TooltipContent>
          </Tooltip>
        </TooltipProvider>
      </div>
    </div>
  );
};
